import * as React from "react";
import Box from "@mui/joy/Box";
import IconButton from "@mui/joy/IconButton";
import Stack from "@mui/joy/Stack";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useUserByIdQuery } from "../../../__generated__/types-and-hooks";
import { formatDistanceToNow } from "date-fns";

export interface ChatMessageProps {
  id: string | number;
  content: string;
  contentType: string;
  timestamp: Date;
  createdBy: number;
}

type ChatBubbleProps = ChatMessageProps & {
  variant: "sent" | "received";
};

export default function ChatBubble({
  id,
  content,
  contentType,
  variant,
  timestamp,
  createdBy,
}: ChatBubbleProps) {
  const userByIdQuery = useUserByIdQuery({ variables: { id: createdBy } });
  const user = userByIdQuery.data?.user_by_id;
  const fullName = user?.firstName + " " + user?.lastName;

  const isSent = variant === "sent";
  const [isHovered, setIsHovered] = React.useState<boolean>(false);
  const [isLiked, setIsLiked] = React.useState<boolean>(false);
  const [isCelebrated, setIsCelebrated] = React.useState<boolean>(false);
  return (
    <Box sx={{ py: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "space-between", mb: 0.25 }}
      >
        <Typography level="body-xs">{fullName}</Typography>
        <Typography level="body-xs">
          {formatDistanceToNow(timestamp, { addSuffix: true })}
        </Typography>
      </Stack>
      <Box
        sx={{ position: "relative" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Sheet
          color={isSent ? "primary" : "neutral"}
          variant={isSent ? "solid" : "soft"}
          sx={[
            {
              p: 1.25,
              borderRadius: "lg",
            },
            isSent
              ? {
                  borderTopRightRadius: 0,
                }
              : {
                  borderTopRightRadius: "lg",
                },
            isSent
              ? {
                  borderTopLeftRadius: "lg",
                }
              : {
                  borderTopLeftRadius: 0,
                },
            isSent
              ? {
                  // backgroundColor: "var(--joy-palette-primary-solidBg)",
                }
              : {
                  // backgroundColor: "background.body",
                },
          ]}
        >
          <Typography
            level="body-sm"
            sx={[
              isSent
                ? {
                    color: "var(--joy-palette-common-white)",
                  }
                : {
                    color: "var(--joy-palette-text-primary)",
                  },
            ]}
          >
            {content}
          </Typography>
        </Sheet>
        {(isHovered || isLiked || isCelebrated) && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              justifyContent: isSent ? "flex-end" : "flex-start",
              position: "absolute",
              top: "50%",
              p: 1.5,
            }}
          >
            <IconButton
              variant={isLiked ? "soft" : "plain"}
              color={isLiked ? "danger" : "neutral"}
              size="sm"
              onClick={() => setIsLiked((prevState) => !prevState)}
            >
              {isLiked ? "❤️" : <FavoriteBorderIcon />}
            </IconButton>
            <IconButton
              variant={isCelebrated ? "soft" : "plain"}
              color={isCelebrated ? "warning" : "neutral"}
              size="sm"
              onClick={() => setIsCelebrated((prevState) => !prevState)}
            >
              {isCelebrated ? "🎉" : <CelebrationOutlinedIcon />}
            </IconButton>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
